import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  KeyIcon,
  UserIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import useApiKeyDetail from './useApiKeyDetail';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function ApiKeyDetail() {
  const location = useLocation();
  const { apiKeyId } = location?.state || {};
  const navigate = useNavigate();
  const { apiKey, subscription, usageMetrics, loading, error, fetchApiKeyDetail } = useApiKeyDetail();

  useEffect(() => {
    if (apiKeyId) fetchApiKeyDetail(apiKeyId);
  }, [apiKeyId, fetchApiKeyDetail]);

  const getStatusPill = (isActive) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
      isActive ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
    }`}>
      {isActive ? <CheckCircleIcon className="h-3 w-3 mr-1" /> : <XCircleIcon className="h-3 w-3 mr-1" />}
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const dailyUsage = usageMetrics?.dailyUsage || [];
  const barData = {
    labels: dailyUsage.map((d) => new Date(d.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Daily API Calls',
        data: dailyUsage.map((d) => d.count || 0),
        backgroundColor: '#3B82F6',
      },
    ],
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64 text-white">Loading API key details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64 text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64 text-white text-lg font-medium">API key not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/Admin/Dashboard/APIKeys')}
            className="flex items-center space-x-2 text-white text-lg font-medium hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to API Keys</span>
          </button>
        </div>
        {/* <div className="text-sm text-white text-lg font-medium">Key ID: {apiKeyId}</div> */}
      </div>

      {/* First Row: Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
  {/* Key Details */}
  <div className="flex flex-col">
    <h2 className="text-2xl font-semibold text-white mb-4">Key Details</h2>
    <div className="bg-blueGray border border-borderGray rounded-lg p-6  flex-1">
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">API Key Name</span>
        <span className="text-white font-medium">{apiKey.label || 'N/A'}</span>
      </div>
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">API Key</span>
        <span className="text-white font-mono text-sm truncate max-w-xs text-right">
          {apiKey.key}
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">Status</span>
        {getStatusPill(apiKey.isActive)}
      </div>
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">Created At</span>
        <span className="text-white">{new Date(apiKey.createdAt).toLocaleString()}</span>
      </div>
      <div className="flex items-center justify-between pt-5">
        <span className="text-white text-lg font-medium">User</span>
        <span className="text-white flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-white text-lg font-medium" />
          <span className="text-sm">{apiKey.user?.name || apiKey.userId?.name}</span>
          <span className=" text-white text-lg font-medium">{apiKey.user?.email || apiKey.userId?.email}</span>
        </span>
      </div>
    </div>
  </div>

  {/* Usage Stats */}
  <div className="flex flex-col">
    <h2 className="text-2xl font-semibold text-white mb-4">Usage Stats</h2>
    <div className="bg-blueGray border border-borderGray rounded-lg p-6 flex-1 relative">
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">Total API Calls</span>
        <span className="text-white font-medium">{usageMetrics?.totalApiCalls ?? 0}</span>
      </div>
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">This Month</span>
        <span className="text-white font-medium">{usageMetrics?.currentMonthUsage ?? 0}</span>
      </div>
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">Monthly Limit</span>
        <span className="text-white font-medium">
          {subscription?.planId?.rateLimits?.monthly ?? usageMetrics?.maxMonthlyUsage ?? 0}
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-borderGray py-5">
        <span className="text-white text-lg font-medium">Today</span>
        <span className="text-white font-medium">{usageMetrics?.todayUsage ?? 0}</span>
      </div>
      <div className="flex items-center justify-between pt-5">
        <span className="text-white text-lg font-medium">Last Used</span>
        <span className="text-white font-medium">
          {usageMetrics?.lastUsed ? new Date(usageMetrics.lastUsed).toLocaleString() : 'N/A'}
        </span>
      </div>

      {/* Bar chart bottom-right */}
      <div className="mt-4">
        <div className="w-full lg:w-2/3 ml-auto">
          <Bar
            data={barData}
            options={{
              responsive: true,
              plugins: { legend: { display: false } },
            }}
          />
        </div>
      </div>
    </div>
  </div>
</div>

    </div>
  );
}


